import { useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { EditModeProvider } from "@/contexts/EditModeContext";
import { ChatProvider, useChat } from "@/contexts/ChatContext";
import { EmailPopup } from "@/components/EmailPopup";
import { EmailPopupTrigger } from "@/components/EmailPopupTrigger";
import { AskCircleMobileFab } from "@/components/AskCircleMobileFab";
import { ChatModal } from "@/components/askCircle/ChatModal";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GeoBlocker } from "@/components/GeoBlocker";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Browse from "./pages/Browse";
import ProductDetail from "./pages/ProductDetail";
import Sell from "./pages/Sell";
import AskCircle from "./pages/AskCircle";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminAddProduct from "./pages/AdminAddProduct";
import AdminBundles from "./pages/admin/AdminBundles";
import AdminRetailers from "./pages/admin/AdminRetailers";
import UpdateHomeJab from "./pages/admin/UpdateHomeJab";
import SecurityDashboard from "./pages/admin/SecurityDashboard";

import PartnerApplications from "./pages/admin/PartnerApplications";
import ReviewModeration from "./pages/admin/ReviewModeration";
import AITraining from "./pages/admin/AITraining";
import VendorReview from "./pages/VendorReview";
import VendorDashboard from "./pages/VendorDashboard";
import VendorEditListing from "./pages/VendorEditListing";
import VendorChangeRequest from "./pages/VendorChangeRequest";
import VendorUpdates from "./pages/admin/VendorUpdates";
import ChangeRequests from "./pages/admin/ChangeRequests";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Pricing from "./pages/Pricing";
import Profile from "./pages/account/Profile";
import MyProducts from "./pages/account/MyProducts";
import Payments from "./pages/account/Payments";
import Credits from "./pages/account/Credits";
import Rewards from "./pages/account/Rewards";
import ProMembership from "./pages/account/ProMembership";
import Favorites from "./pages/account/Favorites";
import TechStack from "./pages/account/TechStack";
import PriceAlerts from "./pages/account/PriceAlerts";
import Compare from "./pages/Compare";
import Terms from "./pages/Terms";
import About from "./pages/About";
import GearBundles from "./pages/GearBundles";
import GearBundleDetail from "./pages/GearBundleDetail";
import BestOf from "./pages/BestOf";
import { Navigate } from "react-router-dom";
import { isFeatureEnabled } from "./lib/featureFlags";

const queryClient = new QueryClient();

// Global Chat Component that shows on all pages
function GlobalChat() {
  const { 
    isChatOpen, 
    isChatMinimized, 
    closeChat, 
    minimizeChat, 
    expandChat, 
    initialQuery, 
    startNewChat, 
    chatKey, 
    isNewChat 
  } = useChat();

  return (
    <ChatModal
      open={isChatOpen}
      onOpenChange={closeChat}
      minimized={isChatMinimized}
      onMinimizedChange={(minimized) => minimized ? minimizeChat() : expandChat()}
      initialQuery={initialQuery}
      onNewChat={startNewChat}
      chatKey={chatKey}
      isNewChat={isNewChat}
    />
  );
}

const App = () => {
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ChatProvider>
          <EditModeProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <GeoBlocker>
                  <ErrorBoundary>
                    <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/browse" element={<Browse />} />
                    <Route path="/product/:slug" element={<ProductDetail />} />
                    <Route path="/compare" element={<Compare />} />
                    <Route path="/sell" element={<Sell />} />
                    <Route path="/ask-circle" element={<AskCircle />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/add-product" element={<AdminAddProduct />} />
                    <Route path="/admin/bundles" element={<AdminBundles />} />
                    <Route path="/admin/retailers" element={<AdminRetailers />} />
                    <Route path="/admin/update-homejab" element={<UpdateHomeJab />} />
                    <Route path="/admin/partner-applications" element={<PartnerApplications />} />
                    <Route path="/admin/vendor-updates" element={<VendorUpdates />} />
                    <Route path="/admin/change-requests" element={<ChangeRequests />} />
                    <Route path="/admin/review-moderation" element={<ReviewModeration />} />
                    <Route path="/admin/ai-training" element={<AITraining />} />
                    <Route path="/admin/security" element={<SecurityDashboard />} />
                    {/* Vendor Pages */}
                    <Route path="/vendor" element={<VendorDashboard />} />
                    <Route path="/vendor/edit/:productId" element={<VendorEditListing />} />
                    <Route path="/vendor/change-request/:productId" element={<VendorChangeRequest />} />
                    {/* Account Pages */}
                    <Route path="/account" element={<Navigate to="/account/profile" replace />} />
                    <Route path="/account/profile" element={<Profile />} />
                    <Route path="/account/products" element={<MyProducts />} />
                    <Route path="/account/payments" element={<Payments />} />
                    <Route path="/account/credits" element={<Credits />} />
                    <Route path="/account/rewards" element={<Rewards />} />
                    <Route path="/account/pro" element={<ProMembership />} />
                    <Route path="/account/favorites" element={<Favorites />} />
                    <Route path="/account/tech-stack" element={<TechStack />} />
                    <Route path="/account/price-alerts" element={<PriceAlerts />} />
                    <Route path="/vendor-review/:token" element={<VendorReview />} />
                    {/* Gear Bundles */}
                    {isFeatureEnabled('bundles') && (
                      <>
                        <Route path="/gearbundles" element={<GearBundles />} />
                        <Route path="/gearbundles/:slug" element={<GearBundleDetail />} />
                      </>
                    )}
                    {/* Best-Of Category Pages */}
                    {isFeatureEnabled('bestOfPages') && (
                      <Route path="/best/:categorySlug" element={<BestOf />} />
                    )}
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                    </Routes>
                  </ErrorBoundary>
                  <ScrollToTop />
                  <EmailPopup triggerOpen={showEmailPopup} onOpenChange={setShowEmailPopup} />
                  <EmailPopupTrigger onTrigger={() => setShowEmailPopup(true)} />
                  <AskCircleMobileFab />
                  <GlobalChat />
                </GeoBlocker>
              </BrowserRouter>
            </TooltipProvider>
          </EditModeProvider>
        </ChatProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
